// src/components/resources/tools/generators/SvgAnimation/SvgAnimationGenerator.tsx

/**
 * @fileoverview SVG Animation Generator — an in-browser studio that produces
 * self-contained, animated SVGs (morphing blobs, mesh gradients, loaders,
 * draw-on lines, glowing orbs) the user can copy as code/CSS or download as a
 * `.svg`.
 *
 * The component is a thin, accessible control surface around the pure
 * `buildSvg()` engine in `./buildSvg`. The live preview and the gallery
 * thumbnails render the EXACT exported markup via a `data:image/svg+xml`
 * `<img>`.
 *
 * Accessibility / reduced motion (per 2025–2026 research):
 *  - The EXPORTED file bakes a `@media (prefers-reduced-motion: reduce)` rule,
 *    which honors the viewer's preference when the SVG is embedded INLINE.
 *  - That media query does NOT fire for an `<img>`-embedded SVG (a W3C sandbox
 *    limitation), so the tool's own `<img>` preview is frozen at the HOST level
 *    via Motion's `useReducedMotion()` when the visitor prefers reduced motion.
 *  - The preview also defaults to FROZEN during SSR and the pre-hydration
 *    window (when the preference is unknowable), so reduced-motion visitors
 *    never see the animated preview while hydration settles — motion-OK
 *    visitors get a static first frame that starts animating post-hydration.
 *  - The preview card's entrance uses the site's SSR-safe `data-reveal`
 *    mechanism (default-visible; hidden start state only under
 *    `(scripting: enabled) and (prefers-reduced-motion: no-preference)`), so
 *    no-JS visitors and crawlers always see the card.
 *
 * Heading note: `GeneratorLayout` owns the page's single document-level
 * heading (h1); this component starts its own headings at the h2 level
 * (guarded by `generator-heading-hierarchy.test.ts`).
 */

'use client'

import { useCallback, useId, useMemo, useState, useSyncExternalStore } from 'react'
import { useReducedMotion } from 'motion/react'
import { Button } from '~/components/ui/Button'
import { sendResourceEvent } from '~/lib/gtag'
import RiDownloadLine from '~/icons/remix/generated/RiDownloadLine'
import RiClipboardLine from '~/icons/remix/generated/RiClipboardLine'
import RiClipboardCheckLine from '~/icons/remix/generated/RiClipboardCheckLine'
import RiMagicLine from '~/icons/remix/generated/RiMagicLine'
import RiCodeLine from '~/icons/remix/generated/RiCodeLine'
import RiCss3Line from '~/icons/remix/generated/RiCss3Line'
import {
  buildCssExport,
  buildSvg,
  colorToHex6,
  isRenderableColor,
  svgToDataUri,
  SVG_PRESETS,
  DEFAULT_SVG_OPTIONS,
  type SvgOptions,
  type SvgPresetId,
} from './buildSvg'

const RESOURCE_SLUG = 'svg-animation-generator'

/**
 * Returns `false` during SSR and the first client render, then `true` once
 * hydrated — via `useSyncExternalStore` (server snapshot `false`, client
 * snapshot `true`), so it's hydration-safe with no setState-in-effect. Used to
 * defer reduced-motion-dependent rendering until after hydration.
 */
function useHydrated(): boolean {
  return useSyncExternalStore(
    () => () => undefined,
    () => true,
    () => false,
  )
}

/** Curated colour pairs used by the “Surprise me” shuffle. */
const COLOR_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['#6366F1', '#22D3EE'],
  ['#EC4899', '#F97316'],
  ['#10B981', '#3B82F6'],
  ['#8B5CF6', '#EC4899'],
  ['#F59E0B', '#EF4444'],
  ['#06B6D4', '#6366F1'],
  ['#14B8A6', '#A3E635'],
]

type BackgroundMode = 'transparent' | 'light' | 'dark' | 'custom'

const BACKGROUND_PRESETS: Record<Exclude<BackgroundMode, 'custom'>, string> = {
  transparent: 'transparent',
  light: '#FFFFFF',
  dark: '#0F172A',
}

interface FieldProps {
  label: string
  htmlFor: string
  children: React.ReactNode
  hint?: string
}

function Field({ label, htmlFor, children, hint }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 dark:text-gray-200">
        {label}
      </label>
      {children}
      {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </div>
  )
}

interface ColorFieldProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}

function ColorField({ id, label, value, onChange }: ColorFieldProps): JSX.Element {
  // The free-text input holds an in-progress draft while focused, so typing an
  // invalid value can't desync the field from the preview/export: only values
  // that pass the export engine's OWN acceptance rule (`isRenderableColor`)
  // are committed to state, an invalid draft shows an inline validation hint,
  // and blur normalizes the field back to the last committed value
  // (`r3408229080`).
  const [draft, setDraft] = useState(value)
  const [editing, setEditing] = useState(false)
  // Re-derive the draft when the COMMITTED value changes from outside this
  // field (Surprise me, the swatch picker, another control) — otherwise a
  // focused text input would keep showing a stale draft until blur
  // (`r3540224143`). Render-time state adjustment (the React-endorsed
  // alternative to an effect); the user's own in-progress typing is preserved
  // because a self-committed change satisfies `value === draft.trim()`.
  const [lastValue, setLastValue] = useState(value)
  if (value !== lastValue) {
    setLastValue(value)
    if (!editing || value !== draft.trim()) setDraft(value)
  }
  const shown = editing ? draft : value
  // The native picker accepts ONLY #rrggbb — feeding it any other committed
  // colour (named, rgb()/hsl(), #rgb, alpha hex) makes the browser coerce the
  // swatch to black, and the next picker interaction would overwrite the
  // user's committed colour with that coercion. Feed it a normalized 6-digit
  // hex when derivable (colorToHex6 is pure, so SSR + hydration agree) and
  // DECOUPLE it (hold the last representable hex) otherwise (`r3540509929`).
  const derivedHex = colorToHex6(value)
  const [pickerHex, setPickerHex] = useState(derivedHex ?? '#000000')
  if (derivedHex !== null && derivedHex !== pickerHex) setPickerHex(derivedHex)
  const invalid = editing && draft.trim() !== '' && !isRenderableColor(draft)
  const hintId = `${id}-color-hint`
  return (
    <Field label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={pickerHex}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-gray-300 bg-transparent p-0.5 dark:border-gray-600"
          aria-label={`${label} colour picker`}
        />
        <input
          type="text"
          value={shown}
          onFocus={() => {
            setDraft(value)
            setEditing(true)
          }}
          onChange={(e) => {
            const next = e.target.value
            setDraft(next)
            if (isRenderableColor(next)) onChange(next.trim())
          }}
          onBlur={() => setEditing(false)}
          spellCheck={false}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? hintId : undefined}
          className={`w-full rounded-md border bg-white px-3 py-2 font-mono text-sm uppercase text-gray-900 focus:outline-none focus:ring-1 dark:bg-gray-800 dark:text-gray-100 ${
            invalid
              ? 'border-red-400 focus:border-red-500 focus:ring-red-500 dark:border-red-500'
              : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600'
          }`}
          aria-label={`${label} hex value`}
        />
      </div>
      {invalid ? (
        <p id={hintId} role="status" className="text-xs text-red-600 dark:text-red-400">
          Not a valid colour — the preview and export keep {value.toUpperCase()}.
        </p>
      ) : null}
    </Field>
  )
}

interface ToggleProps {
  id: string
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  hint?: string
}

function Toggle({ id, label, checked, onChange, hint }: ToggleProps): JSX.Element {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
      />
      <span className="text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-200">{label}</span>
        {hint ? <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{hint}</span> : null}
      </span>
    </label>
  )
}

export interface SvgAnimationGeneratorProps {
  /** Optional tracking slug override (used by landing variants). */
  trackingSlug?: string
}

export function SvgAnimationGenerator({ trackingSlug }: SvgAnimationGeneratorProps = {}): JSX.Element {
  const uidBase = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const slug = trackingSlug ?? RESOURCE_SLUG
  // `useReducedMotion()` reads matchMedia, which differs between the server
  // (always false) and a reduced-motion client — applying it during the first
  // client render would mismatch the SSR HTML. Gate it behind a hydration flag
  // (useSyncExternalStore: server snapshot `false`, client `true`) so the
  // initial render matches the server, then settle to the real preference —
  // without a setState-in-effect.
  //
  // Until hydration the preference is UNKNOWABLE, so the preview defaults to
  // motion-FREE (`freezePreview`): reduced-motion visitors never see the
  // animated preview during the pre-hydration window; motion-OK visitors see
  // a static first frame that starts animating once the preference settles
  // (`r3408243581`). `prefersReduced` (the settled preference) still drives
  // the user-facing "preview is paused" note so it only appears for visitors
  // who actually prefer reduced motion.
  const reducedPreference = useReducedMotion()
  const hydrated = useHydrated()
  const prefersReduced = hydrated && Boolean(reducedPreference)
  const freezePreview = !hydrated || prefersReduced

  const [options, setOptions] = useState<SvgOptions>(DEFAULT_SVG_OPTIONS)
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>('transparent')
  const [customBackground, setCustomBackground] = useState('#0B1120')
  const [copied, setCopied] = useState<null | 'svg' | 'css'>(null)
  // Set when the Clipboard API rejects: holds the exact payload the copy
  // attempted, so the user can still select + copy it manually. Essential for
  // the CSS snippet, which is otherwise rendered NOWHERE on the page — a
  // clipboard failure would make "Copy CSS" a dead end (`r3540509926`).
  const [clipboardFallback, setClipboardFallback] = useState<null | { kind: 'svg' | 'css'; text: string }>(null)

  const setOption = useCallback(<K extends keyof SvgOptions>(key: K, value: SvgOptions[K]) => {
    setOptions((prev) => ({ ...prev, [key]: value }))
  }, [])

  const applyBackground = useCallback(
    (mode: BackgroundMode, custom = customBackground) => {
      setBackgroundMode(mode)
      const next = mode === 'custom' ? custom : BACKGROUND_PRESETS[mode]
      setOptions((prev) => ({ ...prev, background: next }))
    },
    [customBackground],
  )

  // Per-EXPORT namespace: a short deterministic hash of the export-relevant
  // options, so two DIFFERENT exports (different preset OR colours) get disjoint
  // gradient ids + `@keyframes` names. Required because an inlined SVG's
  // `<style>` leaks into the host document — without this, pasting two exports
  // from one session onto one page would collide (the tool's stated promise).
  const exportUid = useMemo(() => {
    const key = `${options.preset}|${options.primary}|${options.secondary}|${options.background}|${options.size}|${options.speed}|${options.grain}|${options.reducedMotion}`
    let h = 5381
    for (let i = 0; i < key.length; i += 1) h = ((h << 5) + h + key.charCodeAt(i)) >>> 0
    return `${uidBase}-${options.preset}-${h.toString(36)}`
  }, [options, uidBase])

  // The exported markup is always the full animated SVG (with the baked
  // reduced-motion media block) — never frozen — so the file animates for
  // viewers who allow motion and self-quiets when inlined for those who don't.
  const svgMarkup = useMemo(() => buildSvg(options, exportUid), [options, exportUid])
  // The preview is host-frozen while the motion preference is unknown
  // (SSR + pre-hydration) AND when the visitor prefers reduced motion, since
  // the in-file media query can't reach an <img>-embedded SVG.
  const previewUri = useMemo(
    () => svgToDataUri(buildSvg(options, exportUid, { freeze: freezePreview })),
    [options, exportUid, freezePreview],
  )

  // Gallery thumbnails — memoized on ONLY the option fields that affect them
  // (they fix size to 96 and background to transparent), so dragging the size
  // slider or changing the background doesn't rebuild every preset's SVG.
  const thumbUris = useMemo(() => {
    const map: Record<string, string> = {}
    for (const preset of SVG_PRESETS) {
      map[preset.id] = svgToDataUri(
        buildSvg(
          {
            preset: preset.id,
            primary: options.primary,
            secondary: options.secondary,
            background: 'transparent',
            size: 96,
            speed: options.speed,
            grain: options.grain,
            reducedMotion: options.reducedMotion,
          },
          `${uidBase}-${preset.id}`,
          { freeze: freezePreview },
        ),
      )
    }
    return map
  }, [options.primary, options.secondary, options.speed, options.grain, options.reducedMotion, uidBase, freezePreview])

  const selectPreset = useCallback(
    (preset: SvgPresetId) => {
      setOption('preset', preset)
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: slug,
        params: { interaction_type: 'select_preset', preset },
      })
    },
    [setOption, slug],
  )

  const shuffle = useCallback(() => {
    const preset = SVG_PRESETS[Math.floor(Math.random() * SVG_PRESETS.length)]?.id ?? 'blob'
    const [primary, secondary] =
      COLOR_PAIRS[Math.floor(Math.random() * COLOR_PAIRS.length)] ?? (['#6366F1', '#22D3EE'] as const)
    setOptions((prev) => ({ ...prev, preset, primary, secondary }))
    sendResourceEvent('interact', {
      resourceType: 'tool',
      resourceSlug: slug,
      params: { interaction_type: 'shuffle', preset },
    })
  }, [slug])

  const copyText = useCallback(
    async (text: string, kind: 'svg' | 'css') => {
      try {
        await navigator.clipboard.writeText(text)
        setCopied(kind)
        setClipboardFallback(null)
        window.setTimeout(() => setCopied(null), 2000)
        sendResourceEvent('complete', {
          resourceType: 'tool',
          resourceSlug: slug,
          params: { interaction_type: `copy_${kind}`, preset: options.preset, outcome: 'copied' },
        })
      } catch {
        // Clipboard can reject (permissions / insecure context). Surface the
        // exact payload in a manual-copy fallback — the SVG markup is also in
        // the code textarea, but the CSS snippet is rendered nowhere else.
        setCopied(null)
        setClipboardFallback({ kind, text })
      }
    },
    [slug, options.preset],
  )

  const downloadSvg = useCallback(
    (variant: 'animated' | 'static') => {
      // The static variant is the frozen first frame — a drop-in
      // reduced-motion / `<picture>` fallback for the animated export. It gets
      // its OWN namespace (`-static`) so that if a user inlines BOTH the static
      // and the animated export for the same settings on one page, the static
      // file's unconditional `animation-play-state:paused` rule can't reach (and freeze) the
      // animated copy's identically-named classes.
      const markup =
        variant === 'static' ? buildSvg(options, `${exportUid}-static`, { freeze: true }) : svgMarkup
      const blob = new Blob([markup], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `adsights-${options.preset}-${variant === 'static' ? 'static' : 'animation'}.svg`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
      sendResourceEvent('complete', {
        resourceType: 'tool',
        resourceSlug: slug,
        params: {
          interaction_type: variant === 'static' ? 'download_static_svg' : 'download_svg',
          preset: options.preset,
          outcome: 'downloaded',
        },
      })
    },
    [svgMarkup, options, exportUid, slug],
  )

  return (
    <div className="mx-auto w-full max-w-7xl">
      <div className="flex w-full flex-col gap-8 lg:flex-row-reverse lg:items-start">
        {/* Preview + export — sticky on desktop so it stays in view while scrolling the controls */}
        <aside className="w-full lg:sticky lg:top-24 lg:w-[26rem] lg:shrink-0">
          {/* Entrance via the site's SSR-safe `data-reveal` mechanism (see
              globals.css + RevealBootstrap): the SSR/no-JS/reduced-motion
              output is fully VISIBLE (opacity:1) — a Motion `initial` here
              serialized `opacity:0` into the SSR HTML, blanking the preview
              card until hydration for no-JS / slow-hydration visitors
              (`r3541146906`). The hidden start state lives inside a
              `@media (scripting: enabled) and (prefers-reduced-motion:
              no-preference)` rule, so it can't flash and never applies where
              it can't be animated away. `--reveal-translate` keeps the same
              8px slide the Motion entrance used. */}
          <div
            data-reveal
            style={{ '--reveal-translate': '0 8px' } as React.CSSProperties}
            className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-800">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Live preview</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={shuffle}
                leftIcon={<RiMagicLine className="h-4 w-4" />}
              >
                Surprise me
              </Button>
            </div>
            {/* Checkerboard so transparency is obvious */}
            <div
              className="flex aspect-square w-full items-center justify-center p-8"
              style={{
                backgroundColor: '#f8fafc',
                backgroundImage:
                  'linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)',
                backgroundSize: '24px 24px',
                backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0px',
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element -- a generated data: URI cannot use next/image, and CSS animations only run for an <img>-loaded SVG */}
              <img
                src={previewUri}
                alt={`Animated SVG preview — ${options.preset}`}
                className="h-full w-full object-contain"
              />
            </div>
            {prefersReduced ? (
              <p className="border-t border-gray-100 px-5 py-2 text-center text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
                Your device prefers reduced motion, so this preview is paused. The downloaded file still
                animates for visitors who allow motion.
              </p>
            ) : null}
          </div>

          {/* Generated code */}
          <div className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3 dark:border-gray-800">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <RiCodeLine className="h-4 w-4 text-indigo-500" />
                SVG code
              </h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyText(svgMarkup, 'svg')}
                  leftIcon={
                    copied === 'svg' ? (
                      <RiClipboardCheckLine className="h-4 w-4" />
                    ) : (
                      <RiClipboardLine className="h-4 w-4" />
                    )
                  }
                >
                  {copied === 'svg' ? 'Copied!' : 'Copy SVG'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    // buildCssExport owns the reduced-motion decision: the
                    // host-level reduced-motion override is emitted only when
                    // "Bake reduced-motion support" is on, so the CSS snippet
                    // matches the SVG/download exports (`r3544590422`).
                    copyText(buildCssExport(options, exportUid), 'css')
                  }
                  leftIcon={<RiCss3Line className="h-4 w-4" />}
                >
                  {copied === 'css' ? 'Copied!' : 'Copy CSS'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => downloadSvg('static')}
                  leftIcon={<RiDownloadLine className="h-4 w-4" />}
                  title="Download the frozen first frame — a reduced-motion / <picture> fallback"
                >
                  Static .svg
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => downloadSvg('animated')}
                  leftIcon={<RiDownloadLine className="h-4 w-4" />}
                >
                  Download .svg
                </Button>
              </div>
            </div>
            <textarea
              readOnly
              value={svgMarkup}
              spellCheck={false}
              aria-label="Generated SVG markup"
              className="h-40 w-full resize-none rounded-b-2xl bg-gray-50 p-4 font-mono text-xs leading-relaxed text-gray-700 focus:outline-none dark:bg-gray-950 dark:text-gray-300"
              onFocus={(e) => e.currentTarget.select()}
            />
            {clipboardFallback ? (
              <div
                role="alert"
                className="border-t border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                    Couldn&apos;t access the clipboard (browser permissions or an insecure context). Select the{' '}
                    {clipboardFallback.kind === 'css' ? 'CSS snippet' : 'SVG markup'} below and copy it manually.
                  </p>
                  <button
                    type="button"
                    onClick={() => setClipboardFallback(null)}
                    className="shrink-0 rounded px-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-amber-300 dark:hover:bg-amber-900/40"
                  >
                    Dismiss
                  </button>
                </div>
                <textarea
                  readOnly
                  value={clipboardFallback.text}
                  spellCheck={false}
                  aria-label={
                    clipboardFallback.kind === 'css'
                      ? 'CSS background snippet — copy manually'
                      : 'SVG markup — copy manually'
                  }
                  className="mt-2 h-32 w-full resize-none rounded-md border border-amber-200 bg-white p-3 font-mono text-xs leading-relaxed text-gray-700 focus:outline-none dark:border-amber-900 dark:bg-gray-950 dark:text-gray-300"
                  onFocus={(e) => e.currentTarget.select()}
                />
              </div>
            ) : null}
          </div>
        </aside>

        {/* Controls — fill the remaining width */}
        <section className="min-w-0 flex-1 space-y-6">
          {/* Preset gallery */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Animation style</h3>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
              {SVG_PRESETS.map((preset) => {
                const active = preset.id === options.preset
                const thumbUri = thumbUris[preset.id]
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => selectPreset(preset.id)}
                    aria-pressed={active}
                    title={preset.description}
                    className={`group flex flex-col items-center gap-2 rounded-xl border p-3 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                      active
                        ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500 dark:bg-indigo-950/40'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-indigo-700'
                    }`}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- generated data: URI, animated SVG requires <img> */}
                    <img src={thumbUri} alt="" aria-hidden="true" className="h-14 w-14" />
                    <span
                      className={`text-center text-xs font-medium ${
                        active ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {preset.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Settings — packed into two columns on wider viewports to use the width */}
          <div className="grid gap-x-6 gap-y-6 sm:grid-cols-2">
            {/* Colours — span both columns, pickers side by side */}
            <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
            <ColorField
              id={`${uidBase}-primary`}
              label="Primary colour"
              value={options.primary}
              onChange={(v) => setOption('primary', v)}
            />
            <ColorField
              id={`${uidBase}-secondary`}
              label="Secondary colour"
              value={options.secondary}
              onChange={(v) => setOption('secondary', v)}
            />
          </div>

          {/* Background — span both columns */}
          <div className="sm:col-span-2">
          <Field label="Background" htmlFor={`${uidBase}-bg`}>
            <div className="flex flex-wrap gap-2" id={`${uidBase}-bg`} role="group" aria-label="Background">
              {(['transparent', 'light', 'dark', 'custom'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => applyBackground(mode)}
                  aria-pressed={backgroundMode === mode}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    backgroundMode === mode
                      ? 'border-indigo-500 bg-indigo-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-indigo-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {mode}
                </button>
              ))}
              {backgroundMode === 'custom' ? (
                <input
                  type="color"
                  value={customBackground}
                  onChange={(e) => {
                    setCustomBackground(e.target.value)
                    applyBackground('custom', e.target.value)
                  }}
                  aria-label="Custom background colour"
                  className="h-8 w-10 cursor-pointer rounded-md border border-gray-300 bg-transparent p-0.5 dark:border-gray-600"
                />
              ) : null}
            </div>
          </Field>
          </div>

          {/* Speed */}
          <Field
            label={`Animation speed — ${options.speed}s per cycle`}
            htmlFor={`${uidBase}-speed`}
            hint="Lower is faster."
          >
            <input
              id={`${uidBase}-speed`}
              type="range"
              min={1}
              max={12}
              step={0.5}
              value={options.speed}
              onChange={(e) => setOption('speed', Number(e.target.value))}
              className="w-full accent-indigo-600"
            />
          </Field>

          {/* Size */}
          <Field label={`Export size — ${options.size}px`} htmlFor={`${uidBase}-size`}>
            <input
              id={`${uidBase}-size`}
              type="range"
              min={120}
              max={640}
              step={20}
              value={options.size}
              onChange={(e) => setOption('size', Number(e.target.value))}
              className="w-full accent-indigo-600"
            />
          </Field>

          {/* Options — span both columns */}
          <div className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:col-span-2">
            <Toggle
              id={`${uidBase}-grain`}
              label="Film-grain texture"
              checked={options.grain}
              onChange={(v) => setOption('grain', v)}
              hint="Adds a subtle, premium noise overlay."
            />
            <Toggle
              id={`${uidBase}-reduced`}
              label="Bake reduced-motion support"
              checked={options.reducedMotion}
              onChange={(v) => setOption('reducedMotion', v)}
              hint="Adds a prefers-reduced-motion rule. It applies when the SVG is embedded inline; browsers can't read motion preference from an <img> tag."
            />
          </div>
          </div>

          <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
            Every export is a single, self-contained <code className="font-mono">.svg</code> — the animation
            travels with the file (CSS keyframes only, no JavaScript). Drop it into HTML, a README, an email,
            or a design tool and it just plays.
          </p>
        </section>
      </div>
    </div>
  )
}

export default SvgAnimationGenerator
